import type { MouseEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { ChevronDownIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxTrigger,
} from '@/components/ui/combobox'
import { VirtualizedComboboxList } from '@/components/ui/virtualized-combobox-list'
import { cn } from '@/shared/lib/utils'
import {
  buildPackedItemSkuComboboxItems,
  filterPackedItemSkuOptions,
  PACKED_ITEMS_SKU_FILTER_MIN_QUERY_LENGTH,
} from './packedItemSkuFilterOptions'

type PackedItemsSkuFilterComboboxProps = {
  id?: string
  className?: string
  items: string[]
  value: string | null
  onValueChange: (sku: string | null) => void
  placeholder: string
  searchPlaceholder: string
  emptyText: string
  typeToSearchText: string
  refineSearchText: string
}

export function PackedItemsSkuFilterCombobox({
  id,
  className,
  items,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  typeToSearchText,
  refineSearchText,
}: PackedItemsSkuFilterComboboxProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(inputValue.trim())
    }, 120)
    return () => {
      window.clearTimeout(handle)
    }
  }, [inputValue])

  const searchResult = useMemo(
    () => filterPackedItemSkuOptions(items, debouncedQuery),
    [debouncedQuery, items]
  )

  const comboboxItems = useMemo(
    () => buildPackedItemSkuComboboxItems(searchResult, value, debouncedQuery),
    [debouncedQuery, searchResult, value]
  )

  const emptyMessage = useMemo(() => {
    if (inputValue.trim().length < PACKED_ITEMS_SKU_FILTER_MIN_QUERY_LENGTH) {
      return typeToSearchText
    }
    if (searchResult.truncated) {
      return refineSearchText
    }
    return emptyText
  }, [
    emptyText,
    inputValue,
    refineSearchText,
    searchResult.truncated,
    typeToSearchText,
  ])

  const showVirtualList = comboboxItems.length > 0

  const triggerLabel = value != null && value !== '' ? value : placeholder

  const handleClear = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onValueChange(null)
  }

  return (
    <Combobox
      virtualized
      id={id}
      items={comboboxItems}
      filteredItems={comboboxItems}
      filter={null}
      autoHighlight={false}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) {
          setInputValue('')
          setDebouncedQuery('')
        }
      }}
      inputValue={inputValue}
      onInputValueChange={(next) => {
        setInputValue(next ?? '')
      }}
      value={value}
      onValueChange={(sku) => {
        onValueChange(sku)
        setOpen(false)
        setInputValue('')
        setDebouncedQuery('')
      }}
      isItemEqualToValue={(left, right) => left === right}
    >
      <ComboboxTrigger
        render={
          <Button
            type='button'
            variant='outline'
            className={cn(
              'h-8 w-full justify-between gap-2 px-3 text-sm font-normal shadow-xs',
              className
            )}
          >
            <span
              className={cn(
                'min-w-0 truncate',
                value == null || value === '' ? 'text-muted-foreground' : undefined
              )}
            >
              {triggerLabel}
            </span>
            <span className='flex shrink-0 items-center gap-0.5'>
              {value != null && value !== '' ? (
                <span
                  role='button'
                  tabIndex={-1}
                  aria-label='Clear'
                  onClick={handleClear}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleClear(event as unknown as MouseEvent)
                    }
                  }}
                  className='rounded p-0.5 opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                >
                  <XIcon className='size-4' />
                </span>
              ) : null}
              <ChevronDownIcon className='size-4 opacity-50' aria-hidden />
            </span>
          </Button>
        }
      />
      <ComboboxContent>
        <ComboboxInput showTrigger={false} placeholder={searchPlaceholder} />
        {showVirtualList ? (
          <VirtualizedComboboxList
            items={comboboxItems}
            open={open}
            getItemKey={(sku) => sku}
            getItemValue={(sku) => sku}
            renderItem={(sku) => <span className='truncate'>{sku}</span>}
          />
        ) : (
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
        )}
        {showVirtualList && searchResult.truncated ? (
          <p className='border-t px-3 py-2 text-xs text-muted-foreground'>
            {refineSearchText}
          </p>
        ) : null}
      </ComboboxContent>
    </Combobox>
  )
}
